import React, {useMemo, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/module-container'
import StickyStack from '../../components/cms/sticky-stack'
import {Box, useSlotRecipe, Button, useBreakpointValue} from '@chakra-ui/react3'
import PriceButtonGroup from '../../components/cms/price-button-group'

import Image from '../../components/cms/image'
import {useReducedMotion} from 'framer-motion'
import {AddIcon} from '../../components/icons'
import RichText from '../../components/cms/rich-text'
import highlightSlotRecipe, {priceButtonGroupVariantMap} from './index.recipe'
import Asset from '../../components/cms/asset'
import {useIntl} from 'react-intl'
import useCTAAction from '../../hooks/useCtaAction'
import {hasValidString, hasValidAsset, isVideoAsset, isFullVideo} from '../../utils/utils'
/**
 * HighlightsStackModule
 *
 * Renders a stacked set of highlight teasers with sticky scroll animation.
 * Each highlight can render either an `asset` (video/image) or a fallback `image`,
 * plus optional text content and CTA/price block.
 *
 * @component
 *
 * @param {string} [moduleId] - CMS module id.
 * @param {string} [moduleType] - CMS module type (e.g. 'leap-highlight-stacks').
 * @param {string} [pageId] - Page id for tracking.
 * @param {string} [anchor] - Anchor id for in-page navigation.
 * @param {'default'|'subtle'|'strong'|'inverted'} [background='default'] - Module background surface.
 * @param {number} [headingLevel] - Base heading level (Highlight uses `h{headingLevel + 1}`).
 * @param {boolean} [enableAnimation=true] - Enables StickyStack animation (disabled when reduced motion).
 * @param {'product'|'lifestyle'} [variant='product'] - Visual variant used by slot recipe.
 * @param {'0%'|'40%'|'80%'} [darken='0%'] - Darken overlay intensity (used by recipe).
 *
 * @param {Array<Highlight>} highlights - List of highlights to render.
 *
 * @returns {JSX.Element}
 */

// Pre-existing SSR/initial-paint srcset hint for DisImage.
// This is a best-effort value that pre-dates token extraction — see recipe for actual layout math.
// DisImage re-measures via ResizeObserver after mount, so the impact is limited to the first fetch.
const ASSET_WIDTHS = {base: '100vw', s: '1190px'}

/**
 * Renders the correct asset element for a highlight based on which assets are provided.
 * Handles four cases:
 *  1. Both image assets  → single <Image> with sources prop (<picture> element, performance-safe)
 *  2. Both assets, ≥1 video → CSS display toggle (both subtrees mounted, controlled by useEffect)
 *  3. Single asset       → single <Asset> or <Image> using whichever prop is provided
 *  4. Legacy image only  → <Image> from the deprecated image prop (backward compat)
 */
const HighlightAsset = ({
    asset,
    assetMobile,
    image,
    mobileVideoRef,
    desktopVideoRef,
    isDesktopViewport,
    darken
}) => {
    const hasDesktopAsset = hasValidAsset(asset)
    const hasMobileAsset = hasValidAsset(assetMobile)

    const pictureAlt = isDesktopViewport
        ? asset?.alt || assetMobile?.alt
        : assetMobile?.alt || asset?.alt

    // Both are image assets → single <picture> element via DisImage sources prop.
    // The browser fetches only the matching source, leaving the other unloaded (performance-safe).
    // <picture> has a single <img> tag, so alt is resolved from the currently displayed source.
    if (hasDesktopAsset && hasMobileAsset && !isVideoAsset(asset) && !isVideoAsset(assetMobile)) {
        return (
            <Image
                {...asset}
                alt={pictureAlt}
                widths={ASSET_WIDTHS}
                objectFit="cover"
                objectPosition="center"
                sources={[
                    {src: assetMobile.disBaseLink || assetMobile.src, breakpoint: 'base'},
                    {src: asset.disBaseLink || asset.src, breakpoint: 's'}
                ]}
            />
        )
    }

    // Both assets set and at least one is a video → CSS display toggle.
    // Videos do not prefetch content (controlled by the preload attribute).
    // However, images inside display:none are still downloaded by the browser,
    // so the image+video mixed case does not satisfy the "avoid loading unused
    // assets" AC for the image side. The fully load-optimized path is above (native <picture>).
    if (hasDesktopAsset && hasMobileAsset) {
        return (
            <>
                {/* Mobile asset — hidden at ≥s breakpoint */}
                <Box display={{base: 'block', s: 'none'}} height="100%">
                    <Asset
                        asset={{...assetMobile, autoplay: false, loop: true}}
                        videoRef={mobileVideoRef}
                        widths={ASSET_WIDTHS}
                        objectFit="cover"
                        objectPosition="center"
                        darken={darken}
                    />
                </Box>
                {/* Desktop asset — hidden below s breakpoint */}
                <Box display={{base: 'none', s: 'block'}} height="100%">
                    <Asset
                        asset={{...asset, autoplay: false, loop: true}}
                        videoRef={desktopVideoRef}
                        widths={ASSET_WIDTHS}
                        objectFit="cover"
                        objectPosition="center"
                        darken={darken}
                    />
                </Box>
            </>
        )
    }

    // Single asset — use whichever is provided
    if (hasDesktopAsset || hasMobileAsset) {
        const effectiveAsset = hasDesktopAsset ? asset : assetMobile
        const effectiveRef = hasDesktopAsset ? desktopVideoRef : mobileVideoRef
        return (
            <Asset
                asset={{...effectiveAsset, autoplay: false, loop: true}}
                videoRef={effectiveRef}
                widths={ASSET_WIDTHS}
                objectFit="cover"
                objectPosition="center"
                darken={darken}
            />
        )
    }

    // Legacy image fallback — for backward compatibility with content authored
    // before the asset field was introduced.
    return <Image {...image} widths={ASSET_WIDTHS} objectFit="cover" objectPosition="center" />
}

HighlightAsset.propTypes = {
    asset: PropTypes.object,
    assetMobile: PropTypes.object,
    image: PropTypes.object,
    mobileVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    desktopVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    isDesktopViewport: PropTypes.bool,
    darken: PropTypes.string
}

const Highlight = ({
    subline,
    headline,
    copy,
    priceProduct,
    cta,
    image,
    asset,
    assetMobile,
    headingLevel,
    trackingContext,
    type,
    overlay,
    variant = 'product',
    desktopImageAlignment = 'right',
    darken = '0%',
    isFullyVisible
}) => {
    const {formatMessage} = useIntl()
    const ctaProps = useCTAAction({
        action: {type: 'overlay', value: overlay}
    })

    const recipe = useSlotRecipe({recipe: highlightSlotRecipe})
    const styles = useMemo(
        () => recipe({variant, darken, desktopImageAlignment}),
        [recipe, variant, darken, desktopImageAlignment]
    )
    const mobileVideoRef = useRef(null)
    const desktopVideoRef = useRef(null)
    const isDesktopViewport = useBreakpointValue({base: false, s: true})

    useEffect(() => {
        // In the dual-asset CSS-toggle path both Asset subtrees are mounted (display:none is CSS-
        // only), so both refs have a non-null current. Only act on the ref whose viewport is
        // currently visible, resolved from the Chakra context via useBreakpointValue above.
        // When only one ref is populated (single-asset or image-only paths), bothMounted is false
        // and the viewport check is skipped — the sole non-null ref is acted on directly.
        const bothMounted = mobileVideoRef.current != null && desktopVideoRef.current != null

        const entries = [
            {ref: mobileVideoRef, asset: assetMobile, active: !isDesktopViewport},
            {ref: desktopVideoRef, asset: asset, active: isDesktopViewport}
        ]

        entries.forEach(({ref, asset: refAsset, active}) => {
            if (ref.current == null) return
            if (bothMounted && !active) {
                // Pause the hidden-viewport video — it may have been playing if the user
                // rotated the device or resized the window.
                ref.current.pause()
                return
            }
            if (!isFullyVisible) {
                // Always pause when scrolled out of sight, including full video that the user
                // may have started — prevents audible playback in the background.
                ref.current.pause()
            } else if (!isFullVideo(refAsset)) {
                // Play animation/direct videos on scroll-in.
                // Full video (videoType="video") requires explicit user interaction — never autoplay.
                ref.current.play(true)
            }
        })
    }, [isFullyVisible, asset, assetMobile, isDesktopViewport])

    return (
        <>
            <Box role="group" aria-label={type} css={styles.root}>
                <Box css={styles.widthContainer} layerStyle="container.page">
                    <Box css={styles.contentContainer}>
                        <Box as={`h${headingLevel + 1}`} css={styles.textContainer}>
                            <RichText css={styles.subline}>{subline}</RichText>
                            <RichText css={styles.headline}>{headline}</RichText>
                            {hasValidString(copy?.text) && (
                                <RichText css={styles.copy}>{copy.text}</RichText>
                            )}
                        </Box>
                        {cta?.label && (
                            <Box css={styles.buttons}>
                                <PriceButtonGroup
                                    price={priceProduct}
                                    cta={cta}
                                    layout="inline"
                                    size="small"
                                    trackingContext={trackingContext}
                                    variant={priceButtonGroupVariantMap[variant]}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box css={styles.assetContainer}>
                        <HighlightAsset
                            asset={asset}
                            assetMobile={assetMobile}
                            image={image}
                            isDesktopViewport={isDesktopViewport}
                            mobileVideoRef={mobileVideoRef}
                            desktopVideoRef={desktopVideoRef}
                            darken={darken}
                        />
                    </Box>
                    <Box css={styles.darkenLayer}></Box>
                </Box>
                {overlay && (
                    <Box css={styles.overlay}>
                        <Button
                            {...ctaProps}
                            size="md"
                            variant="primaryFilled"
                            aria-label={formatMessage({
                                defaultMessage: 'More Information, opens Overlay',
                                id: 'leap.highlights.assitive_msg.more_information'
                            })}
                        >
                            <AddIcon />
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    )
}

const highlightProps = {
    subline: PropTypes.string,
    headline: PropTypes.string,
    type: PropTypes.string,
    copy: PropTypes.object,
    priceProduct: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        valueListPrice: PropTypes.number,
        label: PropTypes.string
    }),
    cta: PropTypes.object,
    image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        text: PropTypes.string
    }),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    headingLevel: PropTypes.number,
    variant: PropTypes.oneOf(['product', 'lifestyle']),
    desktopImageAlignment: PropTypes.oneOf(['right', 'left']),
    darken: PropTypes.oneOf(['0%', '40%', '80%']),
    asset: PropTypes.object,
    assetMobile: PropTypes.object,
    overlay: PropTypes.string,
    isFullyVisible: PropTypes.bool
}

Highlight.propTypes = highlightProps

const HighlightsStackModule = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            background,
            highlights,
            headingLevel,
            enableAnimation,
            variant,
            darken
        },
        ref
    ) => {
        const shouldReduceMotion = useReducedMotion()
        // don't show animation if animation is disabled or motion should be reduced
        const noAnimation = !enableAnimation || shouldReduceMotion

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                spacing="none"
                ref={ref}
                modulePadding="highlightStack"
                layerStyle={null}
            >
                <StickyStack.Container noAnimation={noAnimation}>
                    {highlights.map((highlight, index) => (
                        <StickyStack.Item key={index}>
                            <Highlight
                                headingLevel={headingLevel}
                                variant={variant}
                                darken={darken}
                                {...highlight}
                                trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                            />
                        </StickyStack.Item>
                    ))}
                </StickyStack.Container>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    HighlightsStackModule.displayName = 'HighlightsStackModule'
}

HighlightsStackModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverted']),
    headingLevel: PropTypes.number,
    highlights: PropTypes.arrayOf(PropTypes.shape(highlightProps)).isRequired,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    enableAnimation: PropTypes.oneOf([true, false]),
    variant: PropTypes.oneOf(['product', 'lifestyle']),
    darken: PropTypes.oneOf(['0%', '40%', '80%'])
}

export default HighlightsStackModule
